import { useEffect, useId, useRef, useState } from 'react'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/style.css'
import Portal from '../../components/Basics/Portal'
import {
  buildMetadataPairs,
  formatScalar,
  isEmptyMetadata,
} from './LogMetadata'
import {
  DetailOverlay,
  DetailPanel,
  DetailHeader,
  DetailTitleRow,
  DetailTitle,
  DetailClose,
  DetailMetaRow,
  DetailBody,
  DetailSection,
  DetailSectionTitle,
  DetailMessage,
  DetailKvList,
  DetailKvItem,
  DetailKvKey,
  DetailKvValue,
  DetailJson,
  DetailStack,
  DetailEmpty,
  DetailActions,
  DetailActionButton,
  LevelBadge,
  TimeCell,
} from './PensionLogs.styles'

const CLOSE_MS = 280

const isErrorLevel = (level) => {
  const normalized = String(level || '').toLowerCase()
  return normalized === 'error' || normalized === 'fatal'
}

const serializeMetadata = (metadata) => {
  if (isEmptyMetadata(metadata)) return ''
  if (typeof metadata === 'string') return metadata
  try {
    return JSON.stringify(metadata, null, 2)
  } catch {
    return String(metadata)
  }
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const LogDetailDrawer = ({ log, context, onClose }) => {
  const titleId = useId()
  const panelRef = useRef(null)
  const closeRef = useRef(null)
  const closeTimerRef = useRef(null)
  const settledRef = useRef(true)
  const [copied, setCopied] = useState(false)
  const [view, setView] = useState('tree')
  const [displayedLog, setDisplayedLog] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const finishClose = () => {
    if (settledRef.current) return
    settledRef.current = true
    clearCloseTimer()
    setDisplayedLog(null)
    setIsOpen(false)
    onClose?.()
  }

  const beginClose = () => {
    if (settledRef.current || !isOpen) return
    setIsOpen(false)

    if (prefersReducedMotion()) {
      finishClose()
      return
    }

    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(finishClose, CLOSE_MS)
  }

  useEffect(() => {
    if (log) {
      settledRef.current = false
      clearCloseTimer()
      setDisplayedLog(log)
      setView('tree')
      setCopied(false)

      const frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!settledRef.current) setIsOpen(true)
        })
      })
      return () => window.cancelAnimationFrame(frame)
    }

    if (!settledRef.current) {
      setIsOpen(false)
      if (prefersReducedMotion()) {
        settledRef.current = true
        setDisplayedLog(null)
        return undefined
      }
      clearCloseTimer()
      closeTimerRef.current = window.setTimeout(() => {
        settledRef.current = true
        setDisplayedLog(null)
        closeTimerRef.current = null
      }, CLOSE_MS)
    }
    return undefined
  }, [log])

  useEffect(() => {
    if (!displayedLog || !isOpen) return undefined

    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => {
      closeRef.current?.focus()
    }, 40)

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        beginClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus()
      }
    }
  }, [displayedLog, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      clearCloseTimer()
    },
    []
  )

  if (!displayedLog) return null

  const metadata = displayedLog.metadata
  const empty = isEmptyMetadata(metadata)
  const isObject =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata)
  const pairs = isObject ? buildMetadataPairs(metadata, context, null) : []
  const stack =
    isObject && typeof metadata.stack === 'string' ? metadata.stack : null
  const raw = serializeMetadata(metadata)

  const handleCopy = async () => {
    if (!raw) return
    try {
      await navigator.clipboard.writeText(raw)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  const handlePanelTransitionEnd = (event) => {
    if (event.target !== panelRef.current) return
    if (event.propertyName !== 'transform') return
    if (isOpen) return
    finishClose()
  }

  return (
    <Portal>
      <DetailOverlay $open={isOpen} onClick={beginClose} role="presentation">
        <DetailPanel
          ref={panelRef}
          $open={isOpen}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
          onTransitionEnd={handlePanelTransitionEnd}
        >
          <DetailHeader>
            <DetailTitleRow>
              <DetailTitle id={titleId}>Log detail</DetailTitle>
              <DetailClose ref={closeRef} type="button" onClick={beginClose}>
                Cerrar
              </DetailClose>
            </DetailTitleRow>
            <DetailMetaRow>
              <TimeCell>{displayedLog.createdAt}</TimeCell>
              <LevelBadge
                $tone={isErrorLevel(displayedLog.level) ? 'error' : 'info'}
              >
                {displayedLog.level || '—'}
              </LevelBadge>
              <span>{context}</span>
            </DetailMetaRow>
          </DetailHeader>

          <DetailBody>
            <DetailSection>
              <DetailSectionTitle>Message</DetailSectionTitle>
              <DetailMessage>{displayedLog.message || '—'}</DetailMessage>
            </DetailSection>

            <DetailSection>
              <DetailSectionTitle>Summary</DetailSectionTitle>
              {empty && <DetailEmpty>—</DetailEmpty>}
              {!empty && !isObject && (
                <DetailEmpty>
                  {formatScalar(metadata) ?? String(metadata)}
                </DetailEmpty>
              )}
              {!empty && isObject && pairs.length === 0 && (
                <DetailEmpty>Sin campos resumibles</DetailEmpty>
              )}
              {!empty && isObject && pairs.length > 0 && (
                <DetailKvList>
                  {pairs.map((pair) => (
                    <DetailKvItem key={`${pair.key}-${pair.value}`}>
                      <DetailKvKey>{pair.label}</DetailKvKey>
                      <DetailKvValue title={pair.value}>
                        {pair.value}
                      </DetailKvValue>
                    </DetailKvItem>
                  ))}
                </DetailKvList>
              )}
            </DetailSection>

            <DetailSection>
              <DetailSectionTitle>Metadata</DetailSectionTitle>
              <DetailActions>
                <DetailActionButton
                  type="button"
                  $active={view === 'tree'}
                  onClick={() => setView('tree')}
                  disabled={empty || !isObject}
                >
                  Tree
                </DetailActionButton>
                <DetailActionButton
                  type="button"
                  $active={view === 'raw'}
                  onClick={() => setView('raw')}
                  disabled={empty}
                >
                  Raw
                </DetailActionButton>
                <DetailActionButton
                  type="button"
                  onClick={handleCopy}
                  disabled={!raw}
                >
                  {copied ? 'Copied' : 'Copy'}
                </DetailActionButton>
              </DetailActions>

              {empty && <DetailEmpty>Sin metadata</DetailEmpty>}
              {!empty && view === 'tree' && isObject && (
                <DetailJson>
                  <JsonView src={metadata} collapsed={1} />
                </DetailJson>
              )}
              {!empty && (view === 'raw' || !isObject) && (
                <DetailStack as="pre">{raw}</DetailStack>
              )}
            </DetailSection>

            {stack && (
              <DetailSection>
                <DetailSectionTitle>Stack</DetailSectionTitle>
                <DetailStack as="pre">{stack}</DetailStack>
              </DetailSection>
            )}
          </DetailBody>
        </DetailPanel>
      </DetailOverlay>
    </Portal>
  )
}

export default LogDetailDrawer
